import { useState, useCallback, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import {
    XMarkIcon,
    ArrowLeftIcon,
    CheckIcon,
    MagnifyingGlassIcon,
    ChevronRightIcon,
    LockClosedIcon,
    ArrowRightIcon,
    FunnelIcon,
} from '@heroicons/react/24/outline';
import {
    RocketLaunchIcon,
    ArrowTrendingUpIcon,
    WrenchScrewdriverIcon,
    ArrowPathIcon,
    SparklesIcon,
} from '@heroicons/react/24/solid';
import Pagination from '../../components/navigation/Pagination';
import Loader from '../../components/dataDisplay/Loader';
import {
    useChangelogs,
    useChangelogUnreadCount,
    useChangelogCategories,
    useMarkChangelogRead,
    useMarkSingleChangelogRead,
    ChangelogFilters,
    ChangelogEntry,
} from './api/useChangelog';
import { useStateContext } from '../../contexts/ContextProvider';

// ── Type config ──────────────────────────────────────

const TYPE_CONFIG: Record<string, {
    label: string;
    shortLabel: string;
    bgLight: string;
    textColor: string;
    borderColor: string;
    accentColor: string;
    icon: React.ReactNode;
}> = {
    feature: {
        label: 'Nouvelle fonctionnalité',
        shortLabel: 'Fonctionnalité',
        bgLight: 'bg-blue-50',
        textColor: 'text-blue-600',
        borderColor: 'border-blue-200/60',
        accentColor: '#3b82f6',
        icon: <RocketLaunchIcon className="h-3.5 w-3.5" />,
    },
    improvement: {
        label: 'Amélioration',
        shortLabel: 'Amélioration',
        bgLight: 'bg-emerald-50',
        textColor: 'text-emerald-600',
        borderColor: 'border-emerald-200/60',
        accentColor: '#10b981',
        icon: <ArrowTrendingUpIcon className="h-3.5 w-3.5" />,
    },
    fix: {
        label: 'Correction',
        shortLabel: 'Correction',
        bgLight: 'bg-amber-50',
        textColor: 'text-amber-600',
        borderColor: 'border-amber-200/60',
        accentColor: '#f59e0b',
        icon: <WrenchScrewdriverIcon className="h-3.5 w-3.5" />,
    },
    change: {
        label: 'Changement',
        shortLabel: 'Changement',
        bgLight: 'bg-violet-50',
        textColor: 'text-violet-600',
        borderColor: 'border-violet-200/60',
        accentColor: '#8b5cf6',
        icon: <ArrowPathIcon className="h-3.5 w-3.5" />,
    },
};

const TYPE_OPTIONS = [
    { value: '', label: 'Tout' },
    { value: 'feature', label: 'Fonctionnalité' },
    { value: 'improvement', label: 'Amélioration' },
    { value: 'fix', label: 'Correction' },
    { value: 'change', label: 'Changement' },
];

const PLAN_OPTIONS = [
    { value: '', label: 'Tous les plans' },
    { value: '4', label: 'Découverte' },
    { value: '1', label: 'Communication' },
    { value: '2', label: 'Interaction' },
    { value: '3', label: 'Illimité' },
];

// ── Helpers ──────────────────────────────────────────

function stripHtml(html: string): string {
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
}

function timeAgo(dateStr: string): string {
    const now = new Date();
    const date = new Date(dateStr);
    const diff = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diff < 60) return "à l'instant";
    if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
    if (diff < 86400) return `il y a ${Math.floor(diff / 3600)}h`;
    const days = Math.floor(diff / 86400);
    if (days === 1) return 'hier';
    if (days < 30) return `il y a ${days} jours`;
    const months = Math.floor(days / 30);
    if (months === 1) return 'il y a 1 mois';
    if (months < 12) return `il y a ${months} mois`;
    return `il y a ${Math.floor(months / 12)} an${Math.floor(months / 12) > 1 ? 's' : ''}`;
}

// ── Props ────────────────────────────────────────────

interface ChangelogPanelProps {
    isOpen: boolean;
    onClose: () => void;
}

// ── Component ────────────────────────────────────────

export default function ChangelogPanel({ isOpen, onClose }: ChangelogPanelProps) {
    const { setNotifications, currentFacility } = useStateContext();
    const queryClient = useQueryClient();
    const panelRef = useRef<HTMLDivElement>(null);
    const scrollRef = useRef<HTMLDivElement>(null);

    // Animation state
    const [isAnimating, setIsAnimating] = useState(false);
    const [shouldRender, setShouldRender] = useState(false);

    // View state
    const [selectedEntry, setSelectedEntry] = useState<ChangelogEntry | null>(null);
    const [isDetailView, setIsDetailView] = useState(false);
    const [viewTransition, setViewTransition] = useState<'idle' | 'entering' | 'leaving'>('idle');

    // Filters
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const [type, setType] = useState('');
    const [category, setCategory] = useState('');
    const [planFilter, setPlanFilter] = useState('');
    const [showFilters, setShowFilters] = useState(false);

    const markOneAsReadMutation = useMarkSingleChangelogRead();

    const filters: ChangelogFilters = {
        page,
        per_page: 15,
        search: search || undefined,
        category: category || undefined,
        type: type || undefined,
        min_subscription_id: planFilter || undefined,
    };

    const { data: changelogData, isLoading } = useChangelogs(filters);
    const { data: unreadData } = useChangelogUnreadCount();
    const { data: categories } = useChangelogCategories();

    const markAsRead = useMarkChangelogRead({
        onSuccess: () => {
            setNotifications({ success: 'Tout marqué comme lu.' });
            queryClient.invalidateQueries({
                predicate: (q) =>
                    Array.isArray(q.queryKey) &&
                    typeof q.queryKey[0] === 'string' &&
                    q.queryKey[0].startsWith('/changelogs'),
                refetchActive: true,
            });
        },
    });

    const entries = changelogData?.data || [];
    const meta = changelogData?.meta;
    const unreadCount = unreadData?.unread_count ?? 0;

    // ── Animation ────────────────────────────────────

    useEffect(() => {
        if (isOpen) {
            setShouldRender(true);
            setTimeout(() => setIsAnimating(true), 10);
        } else {
            setIsAnimating(false);
            const timer = setTimeout(() => {
                setShouldRender(false);
                setSelectedEntry(null);
                setIsDetailView(false);
                setViewTransition('idle');
            }, 350);
            return () => clearTimeout(timer);
        }
    }, [isOpen]);

    // Close on click outside (ignore clicks on the sidebar trigger button)
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            if (target.closest?.('[data-changelog-trigger]')) return;
            if (panelRef.current && !panelRef.current.contains(target)) {
                onClose();
            }
        };
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen, onClose]);

    // Escape key
    useEffect(() => {
        const handleEsc = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                if (isDetailView) {
                    handleBack();
                } else {
                    onClose();
                }
            }
        };
        if (isOpen) {
            document.addEventListener('keydown', handleEsc);
        }
        return () => document.removeEventListener('keydown', handleEsc);
    }, [isOpen, isDetailView]);

    // Body scroll lock
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => { document.body.style.overflow = 'unset'; };
    }, [isOpen]);

    // ── Handlers ─────────────────────────────────────

    const handleSelectEntry = useCallback((entry: ChangelogEntry) => {
        setViewTransition('entering');
        setSelectedEntry({ ...entry, is_new: false });

        // Small delay for the crossfade
        setTimeout(() => {
            setIsDetailView(true);
            setViewTransition('idle');
            scrollRef.current?.scrollTo({ top: 0 });
        }, 80);

        if (entry.is_new) {
            const queries = queryClient.getQueryCache().findAll({
                predicate: (q) => Array.isArray(q.queryKey) && typeof q.queryKey[0] === 'string' && q.queryKey[0].startsWith('/changelogs?'),
            });
            for (const q of queries) {
                const old = q.state.data as { data: ChangelogEntry[]; meta: unknown } | undefined;
                if (old) {
                    queryClient.setQueryData(q.queryKey, {
                        ...old,
                        data: old.data.map((e) => e.id === entry.id ? { ...e, is_new: false } : e),
                    });
                }
            }
            const currentUnread = queryClient.getQueryData<{ unread_count: number }>('/changelogs/unread-count');
            if (currentUnread) {
                queryClient.setQueryData('/changelogs/unread-count', {
                    unread_count: Math.max(0, currentUnread.unread_count - 1),
                });
            }
            markOneAsReadMutation.mutate(entry.id);
        }
    }, [queryClient, markOneAsReadMutation]);

    const handleBack = useCallback(() => {
        setViewTransition('leaving');
        setTimeout(() => {
            setIsDetailView(false);
            setSelectedEntry(null);
            setViewTransition('idle');
        }, 80);
    }, []);

    const handleMarkAllRead = useCallback(() => {
        markAsRead.mutate(undefined as unknown as void);
    }, [markAsRead]);

    if (!shouldRender) return null;

    // ── Detail view ──────────────────────────────────

    const renderDetail = () => {
        if (!selectedEntry) return null;

        const tc = TYPE_CONFIG[selectedEntry.type] ?? {
            label: selectedEntry.type, shortLabel: selectedEntry.type,
            bgLight: 'bg-gray-50', textColor: 'text-gray-700', borderColor: 'border-gray-200/60',
            accentColor: '#6b7280', icon: null,
        };

        const publishedDate = selectedEntry.published_at ? new Date(selectedEntry.published_at) : null;
        const facilitySubscriptionId = currentFacility?.subscription_id;
        const isAccessible = !selectedEntry.min_subscription_id || !facilitySubscriptionId ||
            facilitySubscriptionId >= selectedEntry.min_subscription_id;
        const heroImage = selectedEntry.media?.find(m => m.type === 'image');
        const otherMedia = selectedEntry.media?.filter(m => m !== heroImage) ?? [];

        return (
            <div className="clp-detail flex flex-col h-full">
                {/* Detail header */}
                <div className="flex items-center gap-2.5 px-5 py-3.5 border-b border-gray-200/60 flex-shrink-0 bg-white">
                    <button
                        onClick={handleBack}
                        className="flex items-center justify-center w-8 h-8 rounded-full text-gray-400 hover:text-main-color hover:bg-main-color-light transition-all duration-200"
                    >
                        <ArrowLeftIcon className="h-4 w-4" />
                    </button>
                    <div className="flex items-center gap-2 min-w-0">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider ${tc.bgLight} ${tc.textColor}`}>
                            {tc.icon}
                            {tc.shortLabel}
                        </span>
                        {publishedDate && (
                            <span className="text-xs text-gray-400">
                                {publishedDate.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}
                            </span>
                        )}
                    </div>
                    <button onClick={onClose} className="ml-auto text-gray-300 hover:text-gray-500 transition-colors">
                        <XMarkIcon className="h-5 w-5" />
                    </button>
                </div>

                {/* Detail content */}
                <div ref={scrollRef} className="flex-1 overflow-y-auto clp-scroll">
                    {/* Hero image */}
                    {heroImage && (
                        <a
                            href={heroImage.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block overflow-hidden bg-gray-100 transition-all hover:brightness-[0.97]"
                        >
                            <img
                                src={heroImage.url}
                                alt={heroImage.caption || selectedEntry.title}
                                className="w-full max-h-60 object-cover"
                            />
                        </a>
                    )}

                    <div className="px-6 py-6 space-y-5">
                        {/* Badges */}
                        <div className="flex flex-wrap items-center gap-2">
                            <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-600">
                                {selectedEntry.category}
                            </span>
                            {selectedEntry.subscription_name && (
                                <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium border ${
                                    isAccessible
                                        ? 'bg-gradient-to-r from-amber-50 to-orange-50 text-amber-700 border-amber-200/50'
                                        : 'bg-gray-50 text-gray-500 border-gray-200/60'
                                }`}>
                                    {!isAccessible && <LockClosedIcon className="h-3 w-3" />}
                                    {selectedEntry.subscription_name}
                                </span>
                            )}
                            {selectedEntry.is_pinned && (
                                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-yellow-50 text-yellow-700 border border-yellow-200/50">
                                    Épinglé
                                </span>
                            )}
                        </div>

                        {/* Title */}
                        <h3 className="text-[22px] font-bold text-gray-900 leading-[1.3] tracking-[-0.01em]">
                            {selectedEntry.title}
                        </h3>

                        {/* Divider */}
                        <div className="h-px bg-gradient-to-r from-gray-200 via-gray-200 to-transparent" />

                        {/* Description */}
                        {selectedEntry.description && (
                            <div
                                className="changelog-content"
                                dangerouslySetInnerHTML={{ __html: selectedEntry.description }}
                            />
                        )}

                        {/* Media gallery */}
                        {otherMedia.length > 0 && (
                            <div className="space-y-3 pt-2">
                                {otherMedia.map((item, idx) => (
                                    <div key={idx}>
                                        {item.type === 'image' ? (
                                            <a href={item.url} target="_blank" rel="noopener noreferrer"
                                               className="block overflow-hidden rounded-xl border border-gray-200/80 bg-gray-50 transition-all duration-200 hover:border-gray-300 hover:shadow-md">
                                                <img src={item.url} alt={item.caption || selectedEntry.title}
                                                     className="w-full object-cover" loading="lazy" />
                                            </a>
                                        ) : item.type === 'video' ? (
                                            <div className="rounded-xl overflow-hidden border border-gray-200/80 bg-gray-900">
                                                <iframe src={item.url} title={item.caption || selectedEntry.title}
                                                        className="w-full aspect-video"
                                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                        allowFullScreen />
                                            </div>
                                        ) : null}
                                        {item.caption && (
                                            <p className="text-xs text-gray-400 mt-1.5 px-0.5">{item.caption}</p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Footer */}
                        {(selectedEntry.internal_link || !isAccessible) && (
                            <div className="flex items-center gap-4 pt-5 border-t border-gray-200/60">
                                {selectedEntry.internal_link && (
                                    <Link to={selectedEntry.internal_link} onClick={onClose}
                                          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white bg-main-color hover:bg-main-color-dark transition-colors duration-200 group/link shadow-sm">
                                        Voir la fonctionnalité
                                        <ArrowRightIcon className="h-3.5 w-3.5 transition-transform group-hover/link:translate-x-0.5" />
                                    </Link>
                                )}
                                {!isAccessible && (
                                    <Link to="/settings/billing" onClick={onClose}
                                          className="ml-auto inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold bg-gradient-to-r from-amber-50 to-orange-50 text-amber-700 border border-amber-200/50 hover:from-amber-100 hover:to-orange-100 transition-all">
                                        <LockClosedIcon className="h-3.5 w-3.5" />
                                        Mettre à niveau
                                    </Link>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    // ── List view ────────────────────────────────────

    const renderList = () => (
        <div className="clp-list flex flex-col h-full">
            {/* Header */}
            <div className="flex-shrink-0 relative overflow-hidden">
                {/* Background with subtle pattern */}
                <div className="absolute inset-0 bg-gradient-to-br from-main-color via-main-color to-[#004d47]" />
                <div className="absolute inset-0 opacity-[0.07]" style={{
                    backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
                    backgroundSize: '18px 18px',
                }} />

                <div className="relative px-5 pt-5 pb-4">
                    <div className="flex items-start justify-between mb-4">
                        <div>
                            <div className="flex items-center gap-2 mb-1">
                                <SparklesIcon className="h-5 w-5 text-white/70" />
                                <h2 className="text-lg font-bold text-white tracking-[-0.01em]">
                                    Nouveautés
                                </h2>
                            </div>
                            <p className="text-[13px] text-white/50 font-medium">
                                Dernières mises à jour et améliorations
                            </p>
                        </div>
                        <button onClick={onClose} className="mt-0.5 p-1 rounded-lg text-white/40 hover:text-white/80 hover:bg-white/10 transition-all duration-200">
                            <XMarkIcon className="h-5 w-5" />
                        </button>
                    </div>

                    {/* Search */}
                    <div className="relative">
                        <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/30" />
                        <input
                            type="text"
                            placeholder="Rechercher..."
                            value={search}
                            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                            className="w-full pl-9 pr-3 py-2.5 rounded-xl bg-white/10 text-white placeholder-white/30 text-sm font-medium border border-white/10 focus:outline-none focus:bg-white/15 focus:border-white/25 backdrop-blur-sm transition-all duration-200"
                        />
                    </div>
                </div>
            </div>

            {/* Filter bar */}
            <div className="flex-shrink-0 border-b border-gray-200/60 bg-white">
                <div className="flex items-center gap-1.5 px-4 py-2.5">
                    {/* Scrollable type pills */}
                    <div className="flex-1 min-w-0 overflow-x-auto clp-filters-scroll">
                        <div className="flex items-center gap-1.5 w-max">
                            {TYPE_OPTIONS.map((opt) => (
                                <button
                                    key={opt.value}
                                    onClick={() => { setType(opt.value); setPage(1); }}
                                    className={`flex-shrink-0 px-2.5 py-[5px] rounded-lg text-[11px] font-semibold tracking-wide whitespace-nowrap transition-all duration-200 ${
                                        type === opt.value
                                            ? 'bg-main-color text-white shadow-sm'
                                            : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                                    }`}
                                >
                                    {opt.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Fixed right actions */}
                    <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                            onClick={() => setShowFilters(!showFilters)}
                            className={`p-1.5 rounded-lg transition-all duration-200 ${
                                showFilters || category || planFilter
                                    ? 'bg-main-color-light text-main-color'
                                    : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                            }`}
                        >
                            <FunnelIcon className="h-3.5 w-3.5" />
                        </button>

                        {unreadCount > 0 && (
                            <button
                                onClick={handleMarkAllRead}
                                className="flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-semibold text-gray-500 hover:text-main-color hover:bg-main-color-light transition-all duration-200"
                                title="Tout marquer comme lu"
                            >
                                <CheckIcon className="h-3 w-3" />
                                Tout lu
                            </button>
                        )}
                    </div>
                </div>

                {/* Extended filters */}
                {showFilters && (
                    <div className="px-4 pb-2.5 overflow-x-auto clp-filters-scroll">
                        <div className="flex items-center gap-2 w-max">
                            <select
                                value={category}
                                onChange={(e) => { setCategory(e.target.value); setPage(1); }}
                                className="flex-shrink-0 text-[11px] font-medium px-2.5 py-1.5 rounded-lg border border-gray-200 bg-gray-50 text-gray-600 focus:outline-none focus:border-main-color focus:ring-1 focus:ring-main-color/20 transition-all"
                            >
                                <option value="">Toutes catégories</option>
                                {(categories || []).map((c) => (
                                    <option key={c} value={c}>{c}</option>
                                ))}
                            </select>
                            <select
                                value={planFilter}
                                onChange={(e) => { setPlanFilter(e.target.value); setPage(1); }}
                                className="flex-shrink-0 text-[11px] font-medium px-2.5 py-1.5 rounded-lg border border-gray-200 bg-gray-50 text-gray-600 focus:outline-none focus:border-main-color focus:ring-1 focus:ring-main-color/20 transition-all"
                            >
                                {PLAN_OPTIONS.map((opt) => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                )}
            </div>

            {/* Entry list */}
            <div className="flex-1 overflow-y-auto clp-scroll bg-gray-50/30">
                {isLoading ? (
                    <div className="flex items-center justify-center py-20">
                        <Loader useNeutralLoader message="Chargement..." />
                    </div>
                ) : entries.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-20 text-center px-6">
                        <div className="relative mb-5">
                            <div className="absolute inset-0 rounded-full bg-main-color/5 blur-xl scale-150" />
                            <div className="relative p-4 rounded-2xl bg-white border border-gray-200/60 shadow-sm">
                                <SparklesIcon className="w-7 h-7 text-main-color/60" />
                            </div>
                        </div>
                        <p className="text-sm font-semibold text-gray-700 mb-1">Aucune nouveauté</p>
                        <p className="text-xs text-gray-400 leading-relaxed">Les prochaines mises à jour<br />apparaîtront ici.</p>
                    </div>
                ) : (
                    <div className="py-2">
                        {entries.map((entry, idx) => (
                            <EntryCard
                                key={entry.id}
                                entry={entry}
                                index={idx}
                                onClick={() => handleSelectEntry(entry)}
                            />
                        ))}

                        {meta && meta.last_page > 1 && (
                            <div className="px-4 py-3">
                                <Pagination
                                    currentPage={meta.current_page}
                                    totalItems={meta.total}
                                    itemsPerPage={meta.per_page}
                                    onPageChange={setPage}
                                />
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );

    // ── Render ────────────────────────────────────────

    const panelWidth = isDetailView ? '600px' : '420px';

    return (
        <>
            {/* Overlay */}
            <div
                className="fixed inset-0 z-40 transition-all duration-350"
                style={{
                    opacity: isAnimating ? 1 : 0,
                    backgroundColor: 'rgba(0, 43, 40, 0.25)',
                    backdropFilter: isAnimating ? 'blur(2px)' : 'blur(0px)',
                }}
            />

            {/* Panel */}
            <div
                ref={panelRef}
                className="fixed top-0 right-0 h-full bg-white z-50 flex flex-col overflow-hidden"
                style={{
                    width: `min(${panelWidth}, 100vw)`,
                    transform: isAnimating ? 'translateX(0)' : 'translateX(100%)',
                    transition: 'transform 350ms cubic-bezier(0.32, 0.72, 0, 1), width 250ms cubic-bezier(0.32, 0.72, 0, 1)',
                    boxShadow: isAnimating
                        ? '-20px 0 60px -10px rgba(0, 43, 40, 0.15), -4px 0 20px -5px rgba(0, 43, 40, 0.08)'
                        : 'none',
                }}
            >
                <div
                    className="h-full transition-opacity duration-150"
                    style={{ opacity: viewTransition !== 'idle' ? 0.4 : 1 }}
                >
                    {isDetailView ? renderDetail() : renderList()}
                </div>
            </div>

            {/* Scoped styles */}
            <style>{`
                .clp-scroll::-webkit-scrollbar { width: 4px; }
                .clp-scroll::-webkit-scrollbar-track { background: transparent; }
                .clp-scroll::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 4px; }
                .clp-scroll::-webkit-scrollbar-thumb:hover { background: #9ca3af; }

                .clp-filters-scroll { scrollbar-width: none; -ms-overflow-style: none; }
                .clp-filters-scroll::-webkit-scrollbar { display: none; }

                .clp-list .clp-entry {
                    animation: clp-slide-up 0.35s cubic-bezier(0.22, 1, 0.36, 1) both;
                }

                .clp-detail {
                    animation: clp-fade-in 0.3s ease both;
                }

                @keyframes clp-slide-up {
                    from { opacity: 0; transform: translateY(8px); }
                    to { opacity: 1; transform: translateY(0); }
                }

                @keyframes clp-fade-in {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
            `}</style>
        </>
    );
}

// ── Entry Card sub-component ─────────────────────────

function EntryCard({ entry, onClick, index }: { entry: ChangelogEntry; onClick: () => void; index: number }) {
    const tc = TYPE_CONFIG[entry.type] ?? {
        shortLabel: entry.type, bgLight: 'bg-gray-50', textColor: 'text-gray-700',
        borderColor: 'border-gray-200/60', accentColor: '#6b7280', icon: null,
    };

    const snippet = entry.description ? stripHtml(entry.description) : '';
    const thumbnail = entry.media?.find(m => m.type === 'image');

    return (
        <button
            type="button"
            onClick={onClick}
            className="clp-entry w-full text-left group"
            style={{ animationDelay: `${index * 40}ms` }}
        >
            <div className="mx-3 my-1 rounded-xl bg-white border border-gray-200/60 overflow-hidden transition-all duration-200 hover:border-gray-300/80 hover:shadow-md hover:shadow-gray-200/50">
                {/* Accent top bar */}
                <div className="h-[3px]" style={{ background: `linear-gradient(90deg, ${tc.accentColor}, ${tc.accentColor}44)` }} />

                {/* Image banner */}
                {thumbnail && (
                    <div className="w-full h-36 bg-gray-100 overflow-hidden">
                        <img
                            src={thumbnail.url}
                            alt=""
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                            loading="lazy"
                        />
                    </div>
                )}

                <div className="px-4 py-3.5">
                    {/* Meta row */}
                    <div className="flex items-center gap-2 mb-2">
                        {entry.published_at && (
                            <span className="text-[11px] text-gray-400 font-medium tracking-wide">
                                {timeAgo(entry.published_at)}
                            </span>
                        )}
                        {entry.is_new && (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider bg-emerald-50 text-emerald-600 border border-emerald-200/50">
                                <span className="relative flex h-1.5 w-1.5">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                                    <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500" />
                                </span>
                                New
                            </span>
                        )}
                        <span className={`ml-auto inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-bold ${tc.bgLight} ${tc.textColor}`}>
                            {tc.icon}
                        </span>
                    </div>

                    {/* Title */}
                    <h3 className="text-[14px] font-semibold text-gray-900 leading-snug mb-1 group-hover:text-main-color transition-colors duration-200">
                        {entry.title}
                    </h3>

                    {/* Snippet */}
                    {snippet && (
                        <p className="text-[13px] text-gray-500 leading-relaxed line-clamp-2 mb-2.5">
                            {snippet}
                        </p>
                    )}

                    {/* Read more */}
                    <div className="flex items-center gap-1 text-[12px] font-semibold text-gray-400 group-hover:text-main-color transition-colors duration-200">
                        Lire la suite
                        <ChevronRightIcon className="h-3 w-3 transition-transform duration-200 group-hover:translate-x-0.5" />
                    </div>
                </div>
            </div>
        </button>
    );
}
